import { useRef, useEffect } from 'react'
import { PageHero, CtaSection } from '../../components'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import './GioiThieu.css'

const studioImg = new URL('../../assets/images/maxconst-office.jpg', import.meta.url).href

const BREADCRUMBS = [
  { label: 'Trang chủ', path: '/' },
  { label: 'Giới thiệu' },
]

const GioiThieuPage = () => {
  useDocumentTitle('Giới Thiệu - MAXCONST')

  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const onScroll = () => {
      const container = containerRef.current
      const img = imgRef.current
      if (!container || !img) return

      const rect = container.getBoundingClientRect()
      const windowH = window.innerHeight

      // Chỉ chạy khi phần tử trong viewport
      if (rect.bottom < 0 || rect.top > windowH) return

      // Tính phần trăm lướt qua container (0 = vừa xuất hiện từ dưới, 1 = ra khỏi phía trên)
      const scrollRatio = (windowH - rect.top) / (windowH + rect.height)
      
      // Dịch chuyển ảnh đi lên nhẹ bên trong card
      // Khi scrollRatio = 0, ta dịch xuống nhẹ (ví dụ -5%)
      // Khi scrollRatio = 1, ta dịch lên (ví dụ -25%)
      const translateY = -5 - scrollRatio * 20
      img.style.setProperty('--translate-y', `${translateY}%`)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll() // chạy 1 lần ngay khi mount
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="gioi-thieu-page">
      {/* Hero */}
      <PageHero
        tag="Về chúng tôi"
        title="CÔNG TY TNHH THIẾT KẾ VÀ XÂY DỰNG TRỌN GÓI MAXCONST"
        titleHighlight="MAXCONST"
        description="Thương hiệu thiết kế & thi công xây dựng trọn gói uy tín, chuyên nghiệp (Thành viên trực thuộc MAXHOME Group)."
        breadcrumbs={BREADCRUMBS}
      />

      {/* Main Intro Section */}
      <section className="about-intro container section">
        <div className="about-grid">
          <div className="about-image-wrapper">
            <div className="about-image-bg-effect"></div>
            <div className="about-image-card" ref={containerRef}>
              <img
                ref={imgRef}
                src={studioImg}
                alt="Văn phòng MAXCONST"
                className="about-image"
              />
            </div>
          </div>
          <div className="about-content">
            <span className="about-tagline">KIẾN TẠO KHÔNG GIAN SỐNG HOÀN MỸ</span>
            <h2 className="about-title">Uy Tín & Chất Lượng Vượt Trội</h2>
            <p className="about-text">
              <strong>CÔNG TY TNHH THIẾT KẾ VÀ XÂY DỰNG TRỌN GÓI MAXCONST</strong> tự hào là đối tác tin cậy của hàng nghìn gia đình Việt trên con đường kiến tạo tổ ấm mơ ước. Chúng tôi cung cấp giải pháp tư vấn, thiết kế kiến trúc và thi công trọn gói nhà vườn, nhà phố, biệt thự chuyên nghiệp.
            </p>
            <p className="about-text">
              Với triết lý lấy khách hàng làm trọng tâm, mỗi công trình do MAXCONST thực hiện không chỉ dừng lại ở tính thẩm mỹ và công năng sử dụng tối ưu, mà còn được nghiên cứu sâu sắc về phong thủy khoa học giúp mang lại tài lộc, bình an cho gia chủ.
            </p>
            <p className="about-text">
              Chúng tôi cam kết sử dụng nguyên vật liệu chất lượng tốt nhất, kiểm soát chặt chẽ quy trình thi công, bàn giao đúng tiến độ và cam kết tuyệt đối không phát sinh chi phí trong suốt quá trình xây dựng.
            </p>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Core Values Section */}
      <section className="about-values section">
        <div className="container">
          <div className="section-header text-center">
            <span className="section-subtitle">ĐỊNH HƯỚNG PHÁT TRIỂN</span>
            <h2 className="section-title">Tầm Nhìn, Sứ Mệnh & Giá Trị</h2>
          </div>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-eye" />
              </div>
              <h3 className="value-card-title">Tầm Nhìn</h3>
              <p className="value-card-desc">
                Trở thành một trong những thương hiệu thiết kế và thi công xây dựng trọn gói uy tín thuộc hệ sinh thái MAXHOME Group, kiến tạo các công trình bền vững cùng thời gian.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-bullseye" />
              </div>
              <h3 className="value-card-title">Sứ Mệnh</h3>
              <p className="value-card-desc">
                Mang lại không gian sống đẳng cấp, tiện nghi và chuẩn phong thủy cho mỗi gia đình Việt. Giúp chủ đầu tư tiết kiệm thời gian, tối ưu hóa ngân sách và an tâm tuyệt đối khi xây nhà.
              </p>
            </div>

            <div className="value-card">
              <div className="value-icon">
                <i className="fas fa-handshake" />
              </div>
              <h3 className="value-card-title">Giá Trị Cốt Lõi</h3>
              <p className="value-card-desc">
                <strong>TẬN TÂM:</strong> Đặt cả trái tim vào từng viên gạch.<br />
                <strong>UY TÍN:</strong> Giữ trọn chữ tín với khách hàng.<br />
                <strong>SÁNG TẠO:</strong> Không ngừng đổi mới thẩm mỹ.<br />
                <strong>CHẤT LƯỢNG:</strong> Đảm bảo bền vững trọn đời.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="about-why-us container section">
        <div className="section-header text-center">
          <span className="section-subtitle">ƯU THẾ VƯỢT TRỘI</span>
          <h2 className="section-title">Tại Sao Nên Chọn MAXCONST?</h2>
        </div>
        <div className="why-us-grid">
          <div className="why-us-item">
            <div className="why-us-number">01</div>
            <div className="why-us-info">
              <h4 className="why-us-title">Bản vẽ chuẩn phong thủy khoa học</h4>
              <p className="why-us-desc">
                Mọi thiết kế đều được tính toán khoa học dựa trên la bàn phong thủy, tuổi mệnh gia chủ để xác định hướng nhà, hướng bếp, vị trí phòng khách tối ưu nhất.
              </p>
            </div>
          </div>

          <div className="why-us-item">
            <div className="why-us-number">02</div>
            <div className="why-us-info">
              <h4 className="why-us-title">Đội ngũ chuyên gia trên 10 năm kinh nghiệm</h4>
              <p className="why-us-desc">
                Đội ngũ KTS và kỹ sư giám sát của chúng tôi đều tốt nghiệp các trường đại học danh tiếng, có thâm niên triển khai hàng trăm dự án xây dựng thực tế.
              </p>
            </div>
          </div>

          <div className="why-us-item">
            <div className="why-us-number">03</div>
            <div className="why-us-info">
              <h4 className="why-us-title">Dự toán chính xác 95%, không phát sinh</h4>
              <p className="why-us-desc">
                Cam kết lập dự toán bóc tách chi tiết vật tư chính xác nhất trước khi thi công. Hợp đồng rõ ràng, đảm bảo không có chi phí ẩn hay chi phí phát sinh.
              </p>
            </div>
          </div>

          <div className="why-us-item">
            <div className="why-us-number">04</div>
            <div className="why-us-info">
              <h4 className="why-us-title">Quy trình giám sát thi công nghiêm ngặt</h4>
              <p className="why-us-desc">
                Hệ thống kỹ sư túc trực tại công trình, nghiệm thu từng hạng mục sắt thép, bê tông, hoàn thiện, đảm bảo vật liệu chuẩn chỉ đúng theo thiết kế.
              </p>
            </div>
          </div>

          <div className="why-us-item">
            <div className="why-us-number">05</div>
            <div className="why-us-info">
              <h4 className="why-us-title">Hỗ trợ pháp lý & Cấp phép xây dựng</h4>
              <p className="why-us-desc">
                MAXCONST hỗ trợ gia chủ toàn bộ hồ sơ xin phép xây dựng, kiểm tra quy hoạch lộ giới nhanh chóng, giúp công trình khởi công suôn sẻ.
              </p>
            </div>
          </div>

          <div className="why-us-item">
            <div className="why-us-number">06</div>
            <div className="why-us-info">
              <h4 className="why-us-title">Chính sách bảo hành kết cấu dài hạn</h4>
              <p className="why-us-desc">
                Cam kết bảo hành phần khung kết cấu lên tới 10 năm và phần hoàn thiện lên tới 2 năm. Hỗ trợ kỹ thuật 24/7 bất cứ khi nào khách hàng yêu cầu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        title="Bạn Đang Cần Tư Vấn Thiết Kế Hoặc Thi Công Trọn Gói?"
        titleHighlight="Thiết Kế Hoặc Thi Công"
        description="Đăng ký nhận tư vấn miễn phí từ đội ngũ kiến trúc sư và chuyên gia phong thủy của Maxconst ngay hôm nay."
      />
    </div>
  )
}

export default GioiThieuPage
